import {
    QuestionnaireFormData,
    RecursivePartial,
    backendServices,
    isTinCheckUnprocessed,
    isTinNameMatch,
    isWatchlistMatch,
    questionnaireHasFilledAnswers,
} from 'common';
import { SimSubmissionContext, SIM_DOCUMENT_CLASS } from '../../_shared/simDocumentVM';
import { findScheduleForClass } from '../../simSchedules';
import {
    QuestionnaireCustomerIds,
    QuestionnaireCustomerNumbers,
    SimCustomerIds,
    SimCustomerNumbers,
} from '../../useEnsureSimCustomers';
import { SimSkeletons } from '../../useSimSkeletons';
import { SupplierPendingSimRequestFlags } from '../../useSupplierPendingSimRequests';
import { convertQuestionnaireEntryToVM } from '../QuestionnaireSim/QuestionnaireSim.utils';
import {
    PostSignupFormData,
    convertCertificateOfInsuranceFormToVM,
    convertRemittanceFormToVM,
    convertTaxDetailsFormToVM,
} from '..';

export type SubmissionDocKey = string;

/** A row from GET /DocumentType/SupplierSIMRequest the supplier still owes. */
export interface PendingSimRequest {
    SIMDocumentTypeId: number;
    SupplierSIMRequestId: number;
}

export interface SubmissionDoc {
    key: SubmissionDocKey;
    label: string;
    build: () => backendServices.ViewModels.DocumentViewModel | null;
    /** Supporting documents submitted in the same call as the document. */
    files?: File[];
    /**
     * Optional server-side validation run before submit (e.g. tax info
     * validation once those APIs exist). Resolve with an error message to
     * fail the row, or null to proceed.
     */
    validate?: () => Promise<string | null>;
    onSuccessfulSubmission?: () => Promise<void>;
}

/** TEMPORARY: while the workaround is on, a doc can't build without its Customer row. */
function isMissingRequiredCustomer(customerId: number | null, clientSideCustomerCreationEnabled: boolean): boolean {
    return clientSideCustomerCreationEnabled && customerId == null;
}

type ValidateAccountFn = (
    body: backendServices.ViewModels.ValidateAccountRequestViewModel
) => Promise<backendServices.ViewModels.ValidateAccountResponseViewModel>;

type ValidateTinFn = (
    body: backendServices.ViewModels.ValidateTinRequestViewModel
) => Promise<backendServices.ViewModels.ValidateTinResponseViewModel>;

type TinIdentity = {
    BusinessName?: string;
    TIN?: string;
};

const ACH_OUTAGE_MESSAGE =
    'Bank account and routing number could not be verified. Retry, or Skip and click Submit again to continue to the next page.';
const TIN_OUTAGE_MESSAGE =
    'Tax ID could not be verified. Retry, or Skip and click Submit again to continue to the next page.';
const ACH_INVALID_MESSAGE = 'Bank account and routing number could not be verified. Check the numbers and try again.';
const TIN_INVALID_MESSAGE = 'Tax ID could not be verified. Check the number and try again.';

/** Plaid database-insights statuses ending in "fail" mean the numbers did not verify. */
function isAccountVerificationFailure(status: string | undefined): boolean {
    return status != null && status.toLowerCase().includes('fail');
}

/** Which Sovos results block submit. One ValidateTIN call returns both. */
type TinChecks = { name: boolean; watchlist: boolean };

/** A definite failure blocks as invalid; an enabled check Sovos did not run blocks as an outage (Retry/Skip). */
function tinCheckError(
    result: backendServices.ViewModels.ValidateTinResponseViewModel,
    checks: TinChecks
): string | null {
    const nameCode = result.TinNameResult?.TinNameCode;
    const listsCode = result.ListMatchResult?.ListsMatchCode;

    if (
        (checks.name && !isTinCheckUnprocessed(nameCode) && !isTinNameMatch(nameCode)) ||
        (checks.watchlist && isWatchlistMatch(listsCode))
    ) {
        return TIN_INVALID_MESSAGE;
    }
    if ((checks.name && isTinCheckUnprocessed(nameCode)) || (checks.watchlist && isTinCheckUnprocessed(listsCode))) {
        return TIN_OUTAGE_MESSAGE;
    }
    return null;
}

/**
 * ACH account/routing check for the remittance doc — only meaningful for the ACH
 * payment method (Plaid /auth/verify is ACH-specific), so other methods skip it.
 * Fail-closed: a Plaid outage blocks the row unless the buyer opted out of blocking.
 */
async function runRemittanceAchCheck({
    remittance,
    enforceResult,
    validateAccount,
}: {
    remittance: PostSignupFormData['remittance'];
    enforceResult: boolean;
    validateAccount: ValidateAccountFn;
}): Promise<string | null> {
    if (remittance.PreferredPaymentMethod !== 'ACH') {
        return null;
    }
    const account = remittance.AccountNumber?.trim();
    const routing = remittance.ABARoutingNumber?.trim();
    if (!account || !routing) {
        return null;
    }
    try {
        const result = await validateAccount({ AccountDetails: { ACH: { Account: account, Routing: routing } } });
        if (result.Success && isAccountVerificationFailure(result.PlaidResponse?.verification_status)) {
            return enforceResult ? ACH_INVALID_MESSAGE : null;
        }
        return null;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[PostSignup] bank account validation could not run', err);
        return enforceResult ? ACH_OUTAGE_MESSAGE : null;
    }
}

/** Shared supplier TIN check via Sovos. Fail-closed; the caller decides whether a failure blocks. */
async function runTinCheck({
    tin,
    businessName,
    validateTin,
    checks,
}: {
    tin: string | undefined;
    businessName: string | undefined;
    validateTin: ValidateTinFn;
    checks: TinChecks;
}): Promise<string | null> {
    const trimmed = tin?.trim();
    const trimmedBusinessName = businessName?.trim();
    if (!trimmed) {
        return null;
    }

    try {
        const result = await validateTin({ Tin: trimmed, FirstName: trimmedBusinessName });
        return tinCheckError(result, checks);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[PostSignup] TIN validation could not run', err);
        return TIN_OUTAGE_MESSAGE;
    }
}

/**
 * Use Tax Details as the canonical identity when scheduled; otherwise use
 * Remittance's own optional identity. Share one result so parallel validators
 * and individual retries do not duplicate a successful request. Failed checks
 * are evicted so Retry always calls Sovos, including when failures don't block.
 */
function buildSharedTinValidator({
    taxIdentity,
    allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
    validateTin,
    checks,
    validationKey,
    validationCache,
}: {
    taxIdentity: TinIdentity;
    allowTaxAndRemittanceCreationOnBackgroundCheckFailure: boolean;
    validateTin: ValidateTinFn | undefined;
    checks: TinChecks;
    validationKey: string;
    validationCache: Map<string, Promise<string | null>>;
}): (() => Promise<string | null>) | undefined {
    if (!validateTin) {
        return undefined;
    }

    const runOrReuseValidation = () => {
        const cachedValidation = validationCache.get(validationKey);
        if (cachedValidation) {
            return cachedValidation;
        }

        // Keep only the result for the current values. Returning to an older
        // Business Name/TIN pair must validate again.
        validationCache.clear();
        const validationPromise = runTinCheck({
            tin: taxIdentity.TIN,
            businessName: taxIdentity.BusinessName,
            validateTin,
            checks,
        });

        validationCache.set(validationKey, validationPromise);
        validationPromise.then((result) => {
            if (result && validationCache.get(validationKey) === validationPromise) {
                validationCache.delete(validationKey);
            }
        });
        return validationPromise;
    };

    // The check still runs (and is recorded) when it can't block, so the cache keeps
    // the real result and only the message is dropped.
    return () =>
        allowTaxAndRemittanceCreationOnBackgroundCheckFailure
            ? runOrReuseValidation().then(() => null)
            : runOrReuseValidation();
}

/** Remittance pre-submit shared TIN gate followed by its ACH check. */
function buildRemittanceValidator({
    remittance,
    allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
    validateAccount,
    validateSharedTin,
}: {
    remittance: PostSignupFormData['remittance'];
    allowTaxAndRemittanceCreationOnBackgroundCheckFailure: boolean;
    validateAccount: ValidateAccountFn | undefined;
    validateSharedTin: (() => Promise<string | null>) | undefined;
}): (() => Promise<string | null>) | undefined {
    if (!validateAccount && !validateSharedTin) {
        return undefined;
    }

    return async () => {
        if (validateSharedTin) {
            const tinError = await validateSharedTin();
            if (tinError) {
                return tinError;
            }
        }
        return validateAccount
            ? runRemittanceAchCheck({
                  remittance,
                  enforceResult: !allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
                  validateAccount,
              })
            : null;
    };
}

function resolveSimDocumentTypeId(
    schedules: backendServices.ViewModels.SIMDocumentTypeScheduleViewModel[],
    documentClass: number
): number | null {
    return findScheduleForClass(schedules, documentClass)?.Id ?? null;
}

/**
 * Matched on `SIMDocumentTypeId` rather than `DocumentClass` so the stamped
 * `SIMRequestId` belongs to the same schedule row as `SIMDocumentTypeId`:
 * a buyer can have several schedules per class.
 */
function resolveSimRequestId(
    simRequests: BuildDocSubmissionPlanArgs['simRequests'],
    simDocumentTypeId: number | null
): number | undefined {
    if (simDocumentTypeId == null) {
        return undefined;
    }
    return simRequests.find((request) => request.SIMDocumentTypeId === simDocumentTypeId)?.SupplierSIMRequestId;
}

/** Buyer-authored label for the class, when they set one; callers fall back to the built-in label. */
function resolveScheduleDescription(
    schedules: backendServices.ViewModels.SIMDocumentTypeScheduleViewModel[],
    documentClass: number
): string | null {
    return findScheduleForClass(schedules, documentClass)?.Description ?? null;
}

type SimSubmissionBaseContext = Omit<SimSubmissionContext, 'customerId' | 'customerNumber'>;

interface BuildDocSubmissionPlanArgs {
    formData: PostSignupFormData;
    flags: SupplierPendingSimRequestFlags;
    skeletons: SimSkeletons;
    questionnaireSkeletons: Record<number, RecursivePartial<backendServices.ViewModels.DocumentViewModel> | undefined>;
    questionnairesBySubTypeId: Map<number, QuestionnaireFormData>;
    simRequests: PendingSimRequest[];
    baseContext: SimSubmissionBaseContext;
    customerIds: SimCustomerIds | null;
    customerNumbers: SimCustomerNumbers | null;
    questionnaireCustomerIds: QuestionnaireCustomerIds | null;
    questionnaireCustomerNumbers: QuestionnaireCustomerNumbers | null;
    attachmentsByDocKey: Record<SubmissionDocKey, File[]>;
    /** Buyer SIM schedules — `Id` is the SIMDocumentType id for each `DocumentClass`. */
    schedules: backendServices.ViewModels.SIMDocumentTypeScheduleViewModel[];
    /**
     * CSP FeaturesEnabled: AllowTaxAndRemittanceCreationOnBackgroundCheckFailure.
     * The TIN and ACH checks still run — the results are recorded by the backend —
     * so this only stops their results from blocking Tax Details and Remittance.
     */
    allowTaxAndRemittanceCreationOnBackgroundCheckFailure: boolean;
    /** When provided (and the buyer ACH opt-in is on), the remittance doc verifies its ACH numbers before submit. */
    validateAccount?: ValidateAccountFn;
    /** Validity flag blocks on a TIN/name mismatch; fraud flag blocks on a watchlist match, unless blocking is off. */
    tinChecks: TinChecks;

    /** When provided (and the buyer TIN opt-in is on), scheduled documents share one TIN check. */
    validateTin?: ValidateTinFn;
    /** Page-session cache: retains only a successful result for the current supplier tax identity. */
    tinValidationCache?: Map<string, Promise<string | null>>;
    /** CSP FeaturesEnabled: EnableClientSideCustomerCreation */
    clientSideCustomerCreationEnabled?: boolean;
    /** Buyer VatArchiveService data */
    vatArchiveServiceData: backendServices.ViewModels.VatArchiveServiceViewModel[];
    supplierCompanyId: number | undefined;
}

export function buildDocSubmissionPlan({
    formData,
    flags,
    skeletons,
    questionnaireSkeletons,
    questionnairesBySubTypeId,
    simRequests,
    baseContext,
    customerIds,
    customerNumbers,
    questionnaireCustomerIds,
    questionnaireCustomerNumbers,
    attachmentsByDocKey,
    schedules,
    allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
    validateAccount,
    validateTin,
    tinChecks,
    tinValidationCache = new Map<string, Promise<string | null>>(),
    clientSideCustomerCreationEnabled = false,
    vatArchiveServiceData,
    supplierCompanyId,
}: BuildDocSubmissionPlanArgs): SubmissionDoc[] {
    const plan: SubmissionDoc[] = [];
    const taxIdentity = flags.taxDetails ? formData.taxDetails : formData.remittance;
    const tinValidationKey = JSON.stringify([
        baseContext.supplierCompanyId,
        baseContext.buyerCompanyId,
        taxIdentity.BusinessName?.trim() ?? '',
        taxIdentity.TIN?.trim() ?? '',
    ]);
    const validateSharedTin = buildSharedTinValidator({
        taxIdentity,
        allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
        validateTin,
        checks: tinChecks,
        validationKey: tinValidationKey,
        validationCache: tinValidationCache,
    });

    if (flags.taxDetails) {
        plan.push({
            key: 'taxDetails',
            label: 'Tax Details',
            files: attachmentsByDocKey.taxDetails,
            validate: validateSharedTin,
            build: () => {
                const customerId = customerIds?.taxDetails ?? null;
                const customerNumber = customerNumbers?.taxDetails ?? null;
                const skeleton = skeletons.taxDetails;
                if (isMissingRequiredCustomer(customerId, clientSideCustomerCreationEnabled) || !skeleton) {
                    return null;
                }
                const simDocumentTypeId = resolveSimDocumentTypeId(schedules, SIM_DOCUMENT_CLASS.W9);
                return convertTaxDetailsFormToVM(
                    formData.taxDetails,
                    skeleton,
                    { ...baseContext, customerId, customerNumber },
                    simDocumentTypeId,
                    resolveScheduleDescription(schedules, SIM_DOCUMENT_CLASS.W9),
                    resolveSimRequestId(simRequests, simDocumentTypeId)
                );
            },
            onSuccessfulSubmission: async () => {
                const api = new backendServices.Apis.CompanyApi();

                const { TIN, VATNumber } = formData.taxDetails;
                if (VATNumber) {
                    const vatArchiveServiceToUpdate = vatArchiveServiceData[0];
                    if (vatArchiveServiceToUpdate) {
                        await api.putVatArchiveService(vatArchiveServiceToUpdate.ID, {
                            ...vatArchiveServiceToUpdate,
                            VATNumber,
                        });
                    }
                }

                if (TIN && supplierCompanyId) {
                    await api.putCompany(supplierCompanyId, {
                        TIN,
                        IsBuyer: false,
                        IsSupplier: true,
                    });
                }
            },
        });
    }

    if (flags.remittance) {
        plan.push({
            key: 'remittance',
            label: 'Remittance',
            files: attachmentsByDocKey.remittance,
            validate: buildRemittanceValidator({
                remittance: formData.remittance,
                allowTaxAndRemittanceCreationOnBackgroundCheckFailure,
                validateAccount,
                validateSharedTin,
            }),
            build: () => {
                const customerId = customerIds?.remittance ?? null;
                const customerNumber = customerNumbers?.remittance ?? null;
                const skeleton = skeletons.remittance;
                if (isMissingRequiredCustomer(customerId, clientSideCustomerCreationEnabled) || !skeleton) {
                    return null;
                }
                const simDocumentTypeId = resolveSimDocumentTypeId(schedules, SIM_DOCUMENT_CLASS.Remittance);
                return convertRemittanceFormToVM(
                    formData.remittance,
                    taxIdentity.TIN,
                    skeleton,
                    { ...baseContext, customerId, customerNumber },
                    simDocumentTypeId,
                    resolveScheduleDescription(schedules, SIM_DOCUMENT_CLASS.Remittance),
                    resolveSimRequestId(simRequests, simDocumentTypeId)
                );
            },
        });
    }

    if (flags.certificateOfInsurance) {
        plan.push({
            key: 'certificateOfInsurance',
            label: 'Certificate of Insurance',
            files: attachmentsByDocKey.certificateOfInsurance,
            build: () => {
                const customerId = customerIds?.certificateOfInsurance ?? null;
                const customerNumber = customerNumbers?.certificateOfInsurance ?? null;
                const skeleton = skeletons.certificateOfInsurance;
                if (isMissingRequiredCustomer(customerId, clientSideCustomerCreationEnabled) || !skeleton) {
                    return null;
                }
                const simDocumentTypeId = resolveSimDocumentTypeId(schedules, SIM_DOCUMENT_CLASS.InsuranceCertificate);
                return convertCertificateOfInsuranceFormToVM(
                    formData.certificateOfInsurance,
                    skeleton,
                    { ...baseContext, customerId, customerNumber },
                    simDocumentTypeId,
                    resolveScheduleDescription(schedules, SIM_DOCUMENT_CLASS.InsuranceCertificate),
                    resolveSimRequestId(simRequests, simDocumentTypeId)
                );
            },
        });
    }

    for (const entry of formData.questionnaires) {
        const questionnaire = questionnairesBySubTypeId.get(entry.businessDocSubTypeId);
        const skeleton = questionnaireSkeletons[entry.businessDocSubTypeId];
        if (!questionnaire || !skeleton) {
            continue;
        }
        const files = attachmentsByDocKey[`questionnaire:${entry.businessDocSubTypeId}`] ?? [];
        if (!questionnaireHasFilledAnswers(questionnaire.items, entry.answers) && files.length === 0) {
            continue;
        }

        plan.push({
            key: `questionnaire:${entry.businessDocSubTypeId}`,
            label: questionnaire.name,
            files,
            build: () => {
                const customerId = questionnaireCustomerIds?.[entry.businessDocSubTypeId] ?? null;
                const customerNumber = questionnaireCustomerNumbers?.[entry.businessDocSubTypeId] ?? null;
                if (isMissingRequiredCustomer(customerId, clientSideCustomerCreationEnabled)) {
                    return null;
                }
                const simDocumentTypeId = resolveSimDocumentTypeId(schedules, entry.businessDocSubTypeId);
                return convertQuestionnaireEntryToVM(
                    entry,
                    questionnaire,
                    skeleton,
                    { ...baseContext, customerId, customerNumber },
                    simDocumentTypeId,
                    resolveSimRequestId(simRequests, simDocumentTypeId)
                );
            },
        });
    }

    return plan;
}
